import { reachGoal } from './metrika'

export const TOOL_GOALS = {
  invoke: 'tools.invoke',
  success: 'tools.success',
  error: 'tools.error',
} as const

export type ToolProcessingMode = 'local' | 'server' | 'local-first'
export type ToolAnalyticsLocale = 'ru' | 'en'

export type ToolInputMethod = 'upload' | 'camera' | 'microphone'

export type ToolDetectedFormat = 'ean-13' | 'ean-8' | 'upc-a' | 'code-128'

export type ToolCompressionMode = 'quality' | 'target_size'

export type ToolPresetMask =
  | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15
  | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23
  | 24 | 25 | 26 | 27 | 28 | 29 | 30 | 31
export type ToolOutcome = 'changed' | 'unchanged'
export type ToolShiftPreset = '2_2' | '3_3' | '1_3' | 'custom'

export type ToolLayoutPreset =
  `${2 | 4 | 8}up_${'a4' | 'a3'}_${'portrait' | 'landscape'}_m${0 | 5 | 10 | 15}_g${0 | 3 | 5 | 10}_${'across' | 'down'}`

export type ToolPageCountBucket = '1-10' | '11-50' | '51-100' | '101-400'

export type ToolErrorCode =
  | 'invalid_input'
  | 'unsupported_input'
  | 'unsupported_output'
  | 'size_limit'
  | 'target_unreachable'
  | 'processing_failed'
  | 'network_error'
  | 'timeout'
  | 'cancelled'
  | 'unknown_error'

interface ToolAnalyticsContextBase {
  app: string
  domain: string
  route: string
  utility: string
  processingMode: ToolProcessingMode
  compressionMode?: ToolCompressionMode
  inputMethod?: ToolInputMethod
  presetMask?: ToolPresetMask
  outcome?: ToolOutcome
  layoutPreset?: ToolLayoutPreset
  pageCountBucket?: ToolPageCountBucket
  preset?: ToolShiftPreset
}

type ToolAnalyticsLocaleDimensions =
  | {
      locale?: never
      landingLocale?: never
    }
  | {
      locale: ToolAnalyticsLocale
      landingLocale: ToolAnalyticsLocale
    }

type ToolUtmOptionalPresenceDimensions =
  | {
      utmTermPresent?: never
      utmContentPresent?: never
    }
  | {
      utmTermPresent: boolean
      utmContentPresent: boolean
    }

export type ToolAnalyticsContext = ToolAnalyticsContextBase &
  ToolAnalyticsLocaleDimensions &
  ToolUtmOptionalPresenceDimensions

interface ToolAnalyticsPayload extends Record<string, unknown> {
  app: string
  domain: string
  route: string
  utility: string
  processing_mode: ToolProcessingMode
  processing?: ToolProcessingMode
  locale?: string
  landing_locale?: string
  mode?: ToolCompressionMode
  input_method?: ToolInputMethod
  detected_format?: ToolDetectedFormat
  layout_preset?: ToolLayoutPreset
  page_count_bucket?: ToolPageCountBucket
  duration_ms?: number
  error_code?: ToolErrorCode
  preset_mask?: ToolPresetMask
  outcome?: ToolOutcome
  preset?: ToolShiftPreset
  utm_term_present?: boolean
  utm_content_present?: boolean
}

function buildPayload(
  context: ToolAnalyticsContext,
  outcome?: {
    durationMs?: number
    errorCode?: ToolErrorCode
    detectedFormat?: ToolDetectedFormat
  },
): ToolAnalyticsPayload {
  const localeDimensions = boundedLocaleDimensions(context)
  const utmOptionalPresenceDimensions = boundedUtmOptionalPresenceDimensions(context)
  return {
    app: context.app,
    domain: context.domain,
    route: context.route,
    utility: context.utility,
    processing_mode: context.processingMode,
    ...localeDimensions,
    ...(context.compressionMode === undefined ? {} : { mode: context.compressionMode }),
    ...(context.inputMethod === undefined ? {} : { input_method: context.inputMethod }),
    ...(outcome?.detectedFormat === undefined
      ? {}
      : { detected_format: outcome.detectedFormat }),
    ...(context.layoutPreset === undefined ? {} : { layout_preset: context.layoutPreset }),
    ...(context.pageCountBucket === undefined ? {} : { page_count_bucket: context.pageCountBucket }),
    ...(outcome?.durationMs === undefined || !Number.isFinite(outcome.durationMs)
      ? {}
      : { duration_ms: Math.max(0, Math.round(outcome.durationMs)) }),
    ...(outcome?.errorCode === undefined ? {} : { error_code: outcome.errorCode }),
    ...(context.presetMask === undefined ? {} : { preset_mask: context.presetMask }),
    ...(context.outcome === undefined ? {} : { outcome: context.outcome }),
    ...(context.preset === undefined ? {} : { preset: context.preset }),
    ...utmOptionalPresenceDimensions,
  }
}

function boundedLocaleDimensions(
  context: Pick<ToolAnalyticsContextBase, 'processingMode'> & {
    locale?: unknown
    landingLocale?: unknown
  },
): Pick<ToolAnalyticsPayload, 'processing' | 'locale' | 'landing_locale'> {
  if (!isToolAnalyticsLocale(context.locale) || !isToolAnalyticsLocale(context.landingLocale)) {
    return {}
  }
  return {
    processing: context.processingMode,
    locale: context.locale,
    landing_locale: context.landingLocale,
  }
}

function isToolAnalyticsLocale(value: unknown): value is ToolAnalyticsLocale {
  return value === 'ru' || value === 'en'
}

function boundedUtmOptionalPresenceDimensions(context: {
  utmTermPresent?: unknown
  utmContentPresent?: unknown
}): Pick<ToolAnalyticsPayload, 'utm_term_present' | 'utm_content_present'> {
  if (
    typeof context.utmTermPresent !== 'boolean'
    || typeof context.utmContentPresent !== 'boolean'
  ) {
    return {}
  }
  return {
    utm_term_present: context.utmTermPresent,
    utm_content_present: context.utmContentPresent,
  }
}

function trackToolInvoke(context: ToolAnalyticsContext): void {
  reachGoal(TOOL_GOALS.invoke, buildPayload(context))
}

function trackToolSuccess(
  context: ToolAnalyticsContext,
  durationMs: number,
  dimensions?: ToolSuccessDimensions,
): void {
  reachGoal(TOOL_GOALS.success, buildPayload(context, {
    durationMs,
    detectedFormat: dimensions?.detectedFormat,
  }))
}

function trackToolError(context: ToolAnalyticsContext, errorCode: ToolErrorCode): void {
  reachGoal(TOOL_GOALS.error, buildPayload(context, { errorCode }))
}

export interface ToolOperation {
  success: (durationMs: number, dimensions?: ToolSuccessDimensions) => void
  error: (errorCode: ToolErrorCode) => void
}

export interface ToolSuccessDimensions {
  detectedFormat?: ToolDetectedFormat
}

export function startToolOperation(context: ToolAnalyticsContext): ToolOperation {
  let settled = false
  trackToolInvoke(context)

  return {
    success(durationMs, dimensions) {
      if (settled) return
      settled = true
      trackToolSuccess(context, durationMs, dimensions)
    },
    error(errorCode) {
      if (settled) return
      settled = true
      trackToolError(context, errorCode)
    },
  }
}

export function trackToolRejection(
  context: ToolAnalyticsContext,
  errorCode: ToolErrorCode,
): void {
  trackToolError(context, errorCode)
}

/**
 * Legacy Я.Метрика goal helpers for utility-SaaS pages.
 * Goal names are sent as-is using `tools.<utility>.<phase>` until callers are
 * migrated to the bounded generic goal contract above.
 */

const eventName = (utility: string, phase: 'invoke' | 'success' | 'error'): string =>
  `tools.${utility}.${phase}`

/** @deprecated Migrate to trackToolInvoke() and the bounded generic goal contract. */
export function toolInvoke(utility: string, params?: Record<string, unknown>): void {
  reachGoal(eventName(utility, 'invoke'), params)
}

/** @deprecated Migrate to trackToolSuccess() and the bounded generic goal contract. */
export function toolSuccess(
  utility: string,
  durationMs: number,
  params?: Record<string, unknown>,
): void {
  reachGoal(eventName(utility, 'success'), { ...params, duration_ms: durationMs })
}

/** @deprecated Migrate to trackToolError() and the bounded generic goal contract. */
export function toolError(
  utility: string,
  errorCode: string,
  params?: Record<string, unknown>,
): void {
  reachGoal(eventName(utility, 'error'), { ...params, error_code: errorCode })
}
